/**
 * UploadView.tsx — "Nuovo progetto" upload screen AND the analysis progress
 * surface (per Omar's 2026-07-23 UX decision: analysis auto-starts on creation,
 * there is no manual analyze button anywhere, and THIS page shows progress,
 * estimated time, and any failure).
 *
 * Flow: submit → create project (once) → upload each staged file with its
 * pipeline role → if the required roles are all present, start the analysis and
 * switch to progress mode (the "Pipeline AI" sidebar becomes a live tracker).
 * The run itself is server-side (BackgroundTasks on Railway): closing the
 * laptop or losing wifi does not affect it — on reconnect the Audit page and
 * the app-wide watcher rediscover the running job.
 *
 * Resilience details: the created project id is kept in state so a retry after
 * a partial upload failure never creates a duplicate project; per-file upload
 * state lets only the failed files re-send; a missing required role blocks the
 * start with an explicit Italian list of what's missing.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { I, type IconComponent } from "../../components/icons";
import { ApiError, apiErrorMessage } from "../../lib/api";
import { useUploadKbDocument } from "../../lib/kbApi";
import { type UploadFileType } from "../../lib/mock";
import {
  DOC_ROLE_LABEL,
  guessRole,
  missingRolesFromError,
  REQUIRED_ANALYZE_ROLES,
  useAnalysisJob,
  useCreateProject,
  useStartAnalysis,
  useUploadDocument,
  type TenderDocRole,
  type TenderProject,
} from "../../lib/tenderApi";
import { fileToUploadFile, useFilePicker } from "../../lib/useFilePicker";

/** Which document roles forward to the Knowledge Base (Omar, 2026-07-23:
 * Relazione only — the narrative report is KB-worthy; the CME/EP/NP tables and
 * the raw regional price list are not). */
const KB_FORWARD_ROLES = new Set<TenderDocRole>(["info"]);

const FILE_ICON: Record<UploadFileType, IconComponent> = {
  pdf: I.PDF,
  xlsx: I.Excel,
  dwg: I.DWG,
  other: I.File,
};
const FILE_COLOR: Record<UploadFileType, string> = {
  pdf: "#B53D2A",
  xlsx: "#2F7D4E",
  dwg: "#2563A8",
  other: "var(--c-ink-3)",
};

const PIPELINE_STEPS: [string, string][] = [
  ["Estrazione voci CME", "Excel & PDF strutturati"],
  ["OCR listini prezzi", "Confronto regionale"],
  ["Calcolo costi iniziali", "Aggregazione per categoria"],
  ["Rilevazione discrepanze", "Quantità · prezzi · descrizioni"],
];

/** Map job progress (0-100) to the active pipeline step index (0-3). */
export function activePipelineStep(progress: number): number {
  if (progress < 60) return 0; // staging + Phase-1 extraction (the long one)
  if (progress < 85) return 1; // Phase-2 matching vs listini
  if (progress < 100) return 3; // parse + persist + discrepanze
  return 4; // done — every step shows ✓
}

/** The order the role <select> presents the pipeline roles. */
const ROLE_OPTIONS: TenderDocRole[] = [
  "cme",
  "full_price",
  "np",
  "info",
  "regional",
  "drawing",
  "other",
];

type FileState = "staged" | "uploading" | "uploaded" | "failed";

/** A file staged for upload, with the pipeline role it will be sent as. */
interface StagedFile {
  file: File;
  role: TenderDocRole;
  state: FileState;
}

/** Human total size of the staged files ("95,7 MB totali"). */
function fmtTotalSize(staged: StagedFile[]): string {
  const bytes = staged.reduce((sum, s) => sum + s.file.size, 0);
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1).replace(".", ",")} MB totali`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} KB totali`;
  return `${bytes} B totali`;
}

function fmtElapsed(totalSecs: number): string {
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

interface UploadViewProps {
  onBack: () => void;
}

export function UploadView({ onBack }: UploadViewProps): JSX.Element {
  const navigate = useNavigate();
  const [drag, setDrag] = useState(false);
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [surface, setSurface] = useState("");
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [kbNote, setKbNote] = useState<string | null>(null);
  const [missingRoles, setMissingRoles] = useState<TenderDocRole[]>([]);
  // The created project survives retries — a partial upload failure must never
  // create a duplicate project on the second attempt.
  const [project, setProject] = useState<TenderProject | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const createProject = useCreateProject();
  const uploadDocument = useUploadDocument();
  const startAnalysis = useStartAnalysis();
  const kbUpload = useUploadKbDocument();
  const job = useAnalysisJob(jobId).data;

  const analyzing = !!jobId && (!job || job.status === "queued" || job.status === "running");
  const analysisFailed = job?.status === "failed";
  const analysisDone = job?.status === "completed";
  const locked = busy || analyzing || analysisDone;

  // Elapsed clock while the analysis runs.
  const startedAtRef = useRef<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!analyzing) return undefined;
    startedAtRef.current = startedAtRef.current ?? Date.now();
    const t = window.setInterval(() => {
      if (startedAtRef.current) {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }
    }, 1000);
    return () => window.clearInterval(t);
  }, [analyzing]);

  function addFiles(incoming: File[]): void {
    if (locked) return;
    setStaged((prev) => {
      const seen = new Set(prev.map((s) => s.file.name));
      const fresh = incoming
        .filter((f) => !seen.has(f.name))
        .map((file) => ({ file, role: guessRole(file.name), state: "staged" as FileState }));
      return [...prev, ...fresh];
    });
    setMissingRoles([]);
  }

  function setRole(fileName: string, role: TenderDocRole): void {
    setStaged((prev) => prev.map((s) => (s.file.name === fileName ? { ...s, role } : s)));
    setMissingRoles([]);
  }

  function setFileState(fileName: string, state: FileState): void {
    setStaged((prev) => prev.map((s) => (s.file.name === fileName ? { ...s, state } : s)));
  }

  async function submit(): Promise<void> {
    setError(null);
    setMissingRoles([]);
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Inserisci il nome del progetto.");
      return;
    }
    const surfaceM2 = surface.trim() ? Number(surface.replace(",", ".")) : undefined;
    if (surfaceM2 !== undefined && (Number.isNaN(surfaceM2) || surfaceM2 < 0)) {
      setError("La superficie deve essere un numero valido.");
      return;
    }
    setBusy(true);
    try {
      // 1. Create the project exactly once (retries reuse it).
      let proj = project;
      if (!proj) {
        proj = await createProject.mutateAsync({
          name: trimmed,
          city: city.trim() || undefined,
          surfaceM2,
        });
        setProject(proj);
      }

      // 2. Upload every file not yet uploaded; keep going past failures so one
      //    bad file doesn't block the rest.
      let anyFailed = false;
      for (const s of staged) {
        if (s.state === "uploaded") continue;
        setFileState(s.file.name, "uploading");
        try {
          await uploadDocument.mutateAsync({ projectId: proj.id, file: s.file, role: s.role });
          setFileState(s.file.name, "uploaded");
        } catch {
          anyFailed = true;
          setFileState(s.file.name, "failed");
        }
      }
      if (anyFailed) {
        setError("Alcuni file non sono stati caricati. Riprova: verranno reinviati solo quelli.");
        return;
      }

      // 3. Forward the Relazione to the Knowledge Base (product decision
      //    2026-07-23: Relazione only). Strictly best-effort — a KB hiccup must
      //    never block the project or the analysis.
      await forwardToKb(proj, staged);

      // 4. Auto-start the analysis when every required role is present.
      const roles = new Set(staged.map((s) => s.role));
      const missing = REQUIRED_ANALYZE_ROLES.filter((r) => !roles.has(r));
      if (missing.length > 0) {
        setMissingRoles([...missing]);
        return;
      }
      await beginAnalysis(proj.id);
    } catch (e) {
      const missing = missingRolesFromError(e);
      if (missing) {
        setMissingRoles(missing);
      } else {
        setError(apiErrorMessage(e));
      }
    } finally {
      setBusy(false);
    }
  }

  async function forwardToKb(proj: TenderProject, files: StagedFile[]): Promise<void> {
    const candidates = files.filter(
      (s) => KB_FORWARD_ROLES.has(s.role) && /\.(pdf|xlsx|xlsm|txt|md)$/i.test(s.file.name),
    );
    if (candidates.length === 0) return;
    const results = await Promise.allSettled(
      candidates.map((s) =>
        kbUpload.mutateAsync({
          file: s.file,
          title: `${proj.name} — ${s.file.name}`,
          domain: "project",
        }),
      ),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed === 0) {
      setKbNote(
        candidates.length === 1
          ? "Relazione aggiunta alla Knowledge Base ✓"
          : `${candidates.length} documenti aggiunti alla Knowledge Base ✓`,
      );
    } else {
      const unauthorized = results.some(
        (r) => r.status === "rejected" && r.reason instanceof ApiError && r.reason.status === 401,
      );
      setKbNote(
        unauthorized
          ? "Relazione non aggiunta alla Knowledge Base — apri la sezione Knowledge Base per verificare l'accesso, poi ricaricala da lì."
          : "Relazione non aggiunta alla Knowledge Base — puoi ricaricarla dalla sezione Knowledge Base.",
      );
    }
  }

  async function beginAnalysis(projectId: string): Promise<void> {
    // The backend dedups (one active run per project), so a double-click or a
    // retry after reload safely re-latches onto the existing job.
    const j = await startAnalysis.mutateAsync(projectId);
    startedAtRef.current = Date.now();
    setElapsed(0);
    setJobId(j.id);
  }

  async function retryAnalysis(): Promise<void> {
    if (!project) return;
    setError(null);
    try {
      await beginAnalysis(project.id);
    } catch (e) {
      const missing = missingRolesFromError(e);
      if (missing) setMissingRoles(missing);
      else setError(apiErrorMessage(e));
    }
  }

  const filePicker = useFilePicker({ multiple: true, onFiles: addFiles });
  const folderPicker = useFilePicker({ multiple: true, directory: true, onFiles: addFiles });

  const progress = job?.progress ?? (jobId ? 0 : null);
  const activeStep = progress != null ? activePipelineStep(progress) : -1;

  return (
    <div style={{ padding: "28px 36px 60px", maxWidth: 1100, margin: "0 auto", width: "100%" }}>
      <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ marginBottom: 16 }}>
        <span style={{ transform: "scaleX(-1)", display: "inline-flex" }}>
          <I.Arrow size={13} />
        </span>{" "}
        {analyzing ? "Torna al workspace (l'analisi continua)" : "Annulla"}
      </button>

      <div className="t-eyebrow" style={{ marginBottom: 6 }}>
        Nuovo progetto · Step 1 di 1
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 600, letterSpacing: "-0.02em", margin: "0 0 6px" }}>
        Carica documenti del progetto
      </h1>
      <p style={{ color: "var(--c-ink-3)", marginTop: 0, marginBottom: 24 }}>
        L&apos;AI estrae il computo, confronta i listini e calcola i costi per categoria.
        L&apos;analisi parte automaticamente appena i documenti richiesti sono caricati.
      </p>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24, alignItems: "start" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Project meta */}
          <div className="card" style={{ padding: 18 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 140px", gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--c-ink-2)" }}>
                  Nome progetto
                </label>
                <input
                  className="input"
                  value={name}
                  placeholder="Es. Riqualificazione Porta Nuova"
                  onChange={(e) => setName(e.target.value)}
                  disabled={!!project}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--c-ink-2)" }}>
                  Città / Cantiere
                </label>
                <input
                  className="input"
                  value={city}
                  placeholder="Es. Milano"
                  onChange={(e) => setCity(e.target.value)}
                  disabled={!!project}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--c-ink-2)" }}>
                  Superficie (m²)
                </label>
                <input
                  className="input"
                  value={surface}
                  placeholder="Es. 4820"
                  inputMode="decimal"
                  onChange={(e) => setSurface(e.target.value)}
                  disabled={!!project}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>
            </div>
          </div>

          {/* Drop zone */}
          <div
            onDragEnter={(e) => {
              e.preventDefault();
              if (!locked) setDrag(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              if (!locked && e.dataTransfer.files.length > 0) {
                addFiles(Array.from(e.dataTransfer.files));
              }
            }}
            style={{
              border: `1.5px dashed ${drag ? "var(--c-brand)" : "var(--c-line-strong)"}`,
              background: drag ? "var(--c-brand-soft)" : "var(--c-bg-elev)",
              borderRadius: 12,
              padding: "36px 24px",
              textAlign: "center",
              transition: "all var(--tr-fast)",
              opacity: locked ? 0.5 : 1,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                background: "var(--c-brand-soft)",
                color: "var(--c-brand)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              <I.Upload size={22} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>
              Trascina file o cartelle qui
            </div>
            <div style={{ fontSize: 12.5, color: "var(--c-ink-3)" }}>
              PDF · Excel · DWG · IFC · ZIP — fino a 1 GB
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 14 }}>
              <button className="btn btn-sm" onClick={filePicker.open} disabled={locked}>
                Sfoglia file
              </button>
              <button className="btn btn-sm" onClick={folderPicker.open} disabled={locked}>
                Sfoglia cartella
              </button>
            </div>
            {filePicker.input}
            {folderPicker.input}
          </div>

          {/* File list */}
          <div className="card" style={{ overflow: "hidden" }}>
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--c-line)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                Documenti da caricare ({staged.length})
              </div>
              <span className="muted" style={{ fontSize: 12 }}>
                {fmtTotalSize(staged)}
              </span>
            </div>
            <div>
              {staged.length === 0 && (
                <div style={{ padding: "16px", fontSize: 12.5, color: "var(--c-ink-3)" }}>
                  Nessun documento. Trascina o sfoglia i file del progetto (CME, Elenco Prezzi,
                  Nuovi Prezzi, Relazione, listino regionale).
                </div>
              )}
              {staged.map((s, i) => {
                const disp = fileToUploadFile(s.file);
                const Ico = FILE_ICON[disp.type] ?? I.File;
                return (
                  <div
                    key={s.file.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 16px",
                      borderBottom: i < staged.length - 1 ? "1px solid var(--c-line)" : "none",
                    }}
                  >
                    <Ico size={18} style={{ color: FILE_COLOR[disp.type] }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {s.file.name}
                      </div>
                      <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 1 }}>
                        {disp.size}
                        {s.state === "uploaded" && (
                          <span style={{ color: "var(--c-ok)" }}> · caricato ✓</span>
                        )}
                        {s.state === "uploading" && <span> · caricamento…</span>}
                        {s.state === "failed" && (
                          <span style={{ color: "var(--c-err)" }}> · non caricato</span>
                        )}
                      </div>
                    </div>
                    <select
                      className="input"
                      value={s.role}
                      onChange={(e) => setRole(s.file.name, e.target.value as TenderDocRole)}
                      disabled={locked || s.state === "uploaded"}
                      style={{ width: 190, fontSize: 12, padding: "4px 8px" }}
                      aria-label={`Ruolo di ${s.file.name}`}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>
                          {DOC_ROLE_LABEL[r]}
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn btn-ghost btn-icon btn-sm"
                      disabled={locked || s.state === "uploaded"}
                      onClick={() =>
                        setStaged((prev) => prev.filter((x) => x.file.name !== s.file.name))
                      }
                    >
                      <I.X size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sidebar — the AI pipeline: static checklist before the run, LIVE
            progress tracker during/after it. */}
        <div className="card" style={{ padding: 18, position: "sticky", top: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "var(--c-ai-soft)",
                color: "var(--c-ai)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <I.Sparkle size={14} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {analyzing ? "Analisi in corso" : analysisDone ? "Analisi completata" : "Pipeline AI"}
            </div>
          </div>

          {/* Live progress bar (only once a job exists) */}
          {progress != null && (
            <div style={{ marginBottom: 14 }}>
              <div
                style={{
                  height: 6,
                  borderRadius: 3,
                  background: "var(--c-line)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${Math.max(progress, 3)}%`,
                    height: "100%",
                    borderRadius: 3,
                    background: analysisFailed ? "var(--c-err)" : "var(--c-ai)",
                    transition: "width 600ms ease",
                  }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11,
                  color: "var(--c-ink-3)",
                  marginTop: 4,
                }}
              >
                <span>{progress}%</span>
                {analyzing && <span>trascorso: {fmtElapsed(elapsed)}</span>}
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: 12.5 }}>
            {PIPELINE_STEPS.map(([t, sub], i) => {
              const done = activeStep > i || analysisDone;
              const active = !analysisDone && activeStep === i && analyzing;
              return (
                <div key={t} style={{ display: "flex", gap: 10 }}>
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 9,
                      border: `1px solid ${done ? "var(--c-ok)" : active ? "var(--c-ai)" : "var(--c-line-strong)"}`,
                      flexShrink: 0,
                      fontSize: 10,
                      color: done ? "var(--c-ok)" : active ? "var(--c-ai)" : "var(--c-ink-3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "var(--c-bg)",
                    }}
                  >
                    {done ? "✓" : i + 1}
                  </div>
                  <div>
                    <div style={{ fontWeight: 500 }}>
                      {t}
                      {active && "…"}
                    </div>
                    <div style={{ color: "var(--c-ink-3)", fontSize: 11.5 }}>{sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ height: 1, background: "var(--c-line)", margin: "16px 0" }} />

          {analyzing && (
            <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginBottom: 12 }}>
              Tempo stimato: <strong>1–3 ore</strong>. L&apos;analisi gira sul server: puoi navigare
              altrove, chiudere il browser o spegnere il computer — al tuo ritorno troverai i
              risultati. Ti avviseremo qui appena è pronta.
            </div>
          )}
          {!jobId && (
            <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginBottom: 12 }}>
              L&apos;analisi parte automaticamente dopo il caricamento dei 5 documenti richiesti
              (CME, Elenco Prezzi, Nuovi Prezzi, Relazione, listino regionale).
            </div>
          )}

          {kbNote && (
            <div
              style={{
                fontSize: 12,
                color: kbNote.includes("✓") ? "var(--c-ok)" : "var(--c-ink-3)",
                background: "var(--c-bg-elev)",
                borderRadius: 8,
                padding: "8px 10px",
                marginBottom: 12,
              }}
            >
              {kbNote}
            </div>
          )}
          {missingRoles.length > 0 && (
            <div
              style={{
                fontSize: 12,
                color: "var(--c-warn)",
                background: "var(--c-warn-soft, rgba(180,120,0,0.08))",
                borderRadius: 8,
                padding: "8px 10px",
                marginBottom: 12,
              }}
            >
              Analisi non avviata — manca: {missingRoles.map((r) => DOC_ROLE_LABEL[r]).join(", ")}.
              Aggiungi i file mancanti e riprova.
            </div>
          )}
          {error && (
            <div
              style={{
                fontSize: 12,
                color: "var(--c-err)",
                background: "var(--c-err-soft)",
                borderRadius: 8,
                padding: "8px 10px",
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}
          {analysisFailed && (
            <div
              style={{
                fontSize: 12,
                color: "var(--c-err)",
                background: "var(--c-err-soft)",
                borderRadius: 8,
                padding: "8px 10px",
                marginBottom: 12,
              }}
            >
              Analisi fallita{job?.error ? `: ${job.error}` : "."}
            </div>
          )}

          {analysisDone ? (
            <button
              className="btn btn-primary"
              onClick={() => project && navigate(`/audit/${project.id}`)}
              style={{ width: "100%", justifyContent: "center", height: 36 }}
            >
              Apri il progetto <I.Arrow size={14} />
            </button>
          ) : analysisFailed ? (
            <button
              className="btn btn-primary"
              onClick={() => void retryAnalysis()}
              disabled={startAnalysis.isPending}
              style={{ width: "100%", justifyContent: "center", height: 36 }}
            >
              <I.Refresh size={14} /> Riprova analisi
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => void submit()}
              disabled={locked || !name.trim()}
              style={{ width: "100%", justifyContent: "center", height: 36 }}
            >
              {busy ? (
                "Creazione in corso…"
              ) : analyzing ? (
                "Analisi in corso…"
              ) : project ? (
                <>
                  <I.Plus size={14} /> Carica e avvia l&apos;analisi
                </>
              ) : (
                <>
                  <I.Plus size={14} /> Crea progetto
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
